import { getActivityCalculatorComponent } from '../api/cmsApi';
import jsonData from './config.json';
import { setThemedCssVariables, setFontFamilyCssVariables } from './setThemedCssVariables';

export async function getConfigByMerchantCode (merchantCode: string) {
  try {
    const config = await JSON.parse(JSON.stringify(jsonData));
    const customConfiguration = merchantCode && merchantConfigs.find(c => c.codes.includes(merchantCode));
    const additionalConfig = customConfiguration || { ...defaultConfig, code: (merchantCode || "CARBONCLICK") };

    const transactionDetailComponent = await getActivityCalculatorComponent(merchantCode)
    const themeFromApi = transactionDetailComponent?.data?.theme;
  
    if (themeFromApi) {
      const theme = {
        palette: {
          primary: themeFromApi.palette.primary,
          secondary: themeFromApi.palette.secondary,
          accent: themeFromApi.palette.accent,
        },
        logo: themeFromApi.logo,
      }
  
      config.theme = theme;
    }
    
    if (transactionDetailComponent.data) {
      setThemedCssVariables(transactionDetailComponent.data.theme);
      setFontFamilyCssVariables(additionalConfig.fontConfig);
    }

    return {
      ...config,
      ...additionalConfig,
    };
  } catch (error: any) {
    const errorResonse = error?.errors && { message: error?.errors[0]?.message, code: error?.errors[0]?.code };
    const suctomError = { code: null, message: 'Something went wrong'};
    throw errorResonse || suctomError;
  }
}

const defaultConfig = {
  displayPoweredBy: true,
  fontConfig: {
    body: "Public Sans",
    heading: "Open Sans"
  },
  ratioSelection: [0.5, 1, 1.5, 2.0],
  defaultRatioSelection: 1,
  tabsConfig: [
    {
      name: 'FLIGHT',
      subtitle: 'Enter your flight details below to learn about your journey’s impact on the planet.',
      light: 'https://res.cloudinary.com/carbonclick/image/upload/v1690908618/widgets/Journey%20Widget/airplane-light_y9x5ii.svg',
      dark: 'https://res.cloudinary.com/carbonclick/image/upload/v1690908618/widgets/Journey%20Widget/airplane-dark_mxczzh.svg',
    },
    {
      name: 'CAR',
      subtitle: 'Enter your driving departure and arrival details below to learn about your journey’s impact on the planet.',
      light: 'https://res.cloudinary.com/carbonclick/image/upload/v1690908618/widgets/Journey%20Widget/car-light_esatzt.svg',
      dark: 'https://res.cloudinary.com/carbonclick/image/upload/v1690908618/widgets/Journey%20Widget/car-dark_kekd0q.svg',
    },
    {
      name: 'PUBLIC_TRANSPORT',
      subtitle: 'Enter your public transport details below to learn about your journey’s impact on the planet',
      light: 'https://res.cloudinary.com/carbonclick/image/upload/v1690908619/widgets/Journey%20Widget/train-light_vl878l.svg',
      dark: 'https://res.cloudinary.com/carbonclick/image/upload/v1690908619/widgets/Journey%20Widget/train-dark_anaaqb.svg',
    },
    {
      name: 'MOTORBIKE',
      subtitle: 'Enter your motorbike departure and destination details below to learn about your journey’s impact on the planet',
      light: 'https://res.cloudinary.com/carbonclick/image/upload/v1690908618/widgets/Journey%20Widget/motorbike-light_f9zi3u.svg',
      dark: 'https://res.cloudinary.com/carbonclick/image/upload/v1690908618/widgets/Journey%20Widget/motorbike-dark_mjfgv8.svg',
    },
  ],
  termsAndConditionsHtml: `<p>By completing the purchase process on CarbonClick, you agree to our 
    <a href="https://www.carbonclick.com/legal/terms-of-trade/" target="_blank" rel="noopener noreferrer">Terms of Sale</a> and 
    <a href="https://www.carbonclick.com/legal/privacy-policy/" target="_blank" rel="noopener noreferrer">Privacy Policy</a>, including the purposes for processing personal data.</p>`,
};

const merchantConfigs = [
  {
    codes: ['FORMULA_1_GLOBAL'],
    displayPoweredBy: false,
    fontConfig: {
      body: "Titillium Web, Arial, Helvetica Neue, Helvetica, sans-serif",
      heading:"Formula One"
    },
    tabsConfig: [
      {
        name: 'FLIGHT',
        subtitle: 'Enter your journey details below to calculate your impact on the planet.',
        light: 'https://res.cloudinary.com/carbonclick/image/upload/v1690909510/widgets/Journey%20Widget/airplane-f1-light_st0twa.svg',
        dark: 'https://res.cloudinary.com/carbonclick/image/upload/v1690909510/widgets/Journey%20Widget/airplane-f1-dark_cmlwy8.svg',
      },
      {
        name: 'CAR',
        subtitle: 'Enter your journey details below to calculate your impact on the planet.',
        light: 'https://res.cloudinary.com/carbonclick/image/upload/v1690909512/widgets/Journey%20Widget/car-f1-light_zukwiq.svg',
        dark: 'https://res.cloudinary.com/carbonclick/image/upload/v1690909512/widgets/Journey%20Widget/car-f1-dark_isxir3.svg',
      },
      {
        name: 'PUBLIC_TRANSPORT',
        subtitle: 'Enter your journey details below to calculate your impact on the planet.',
        light: 'https://res.cloudinary.com/carbonclick/image/upload/v1690909517/widgets/Journey%20Widget/train-f1-light_c6jneo.svg',
        dark: 'https://res.cloudinary.com/carbonclick/image/upload/v1690909517/widgets/Journey%20Widget/train-f1-dark_r8i0y1.svg',
      },
      {
        name: 'MOTORBIKE',
        subtitle: 'Enter your journey details below to calculate your impact on the planet.',
        light: 'https://res.cloudinary.com/carbonclick/image/upload/v1690909515/widgets/Journey%20Widget/moto-f1-light_qqnrnr.svg',
        dark: 'https://res.cloudinary.com/carbonclick/image/upload/v1690909515/widgets/Journey%20Widget/moto-f1-dark_skzvvq.svg',
      },
    ]
  },
  {
    ...defaultConfig,
    codes: [
      'BUS_AE_EXPO_CITY_DUBAI',
      'BUS_AE_EXPO_EVENT',
      'BUS_AE_EXPO_TICKETMASTER',
      'BUS_AE_EXPO_PLATINUMLIST',
      'BUS_AE_EXPO_CVENT',
      'BUS_AE_EXPO_EMPLOYEE',
      'BUS_AE_EXPO_CUSTOMER',
      'BUS_AE_EXPO_PHYSICAL1',
      'BUS_AE_EXPO_PHYSICAL2'
    ],
    showMarketingConsent: true,
    marketingConsentText: 'I agree to receive communications from Expo City Dubai about news, events, and promotions.',
    termsAndConditionsHtml: `<p>By completing the purchase process on CarbonClick, you agree to CarbonClick’s 
      <a href="https://www.carbonclick.com/legal/terms-of-trade/" target="_blank" rel="noopener noreferrer">Terms of Sale</a>, 
      <a href="https://www.carbonclick.com/legal/privacy-policy/" target="_blank" rel="noopener noreferrer">Privacy Policy</a> and Expo City Dubai’s
      <a href="" target="_blank" rel="noopener noreferrer">Terms and Conditions</a>, including the purposes for processing personal data.</p>`,
  }
];
